import styled from 'styled-components';
import { colors } from 'vars';

export interface StyledButtonRoundedProps {
  $disabled: boolean;
  $outlined: boolean;
  $withIcon: boolean;
  $sizeHorizontal: 'small' | 'medium' | 'large';
  $sizeVertical: 'small' | 'medium' | 'large';
  $isLoading: boolean;
  $variant: 'primary' | 'danger' | 'primary-new' | 'dotted';
  $isFullWidth: boolean;
}

const getTextColor = ({
  $disabled,
  $outlined,
  $variant,
}: Pick<StyledButtonRoundedProps, '$disabled' | '$outlined' | '$variant'>) => {
  if ($disabled) {
    return $outlined ? colors.grey01300 : colors.grey01700;
  }

  if ($outlined) {
    if ($variant === 'danger') return colors.red02000;
    if ($variant === 'dotted') return colors.grey10;
    return colors.green01700;
  }

  return colors.white;
};

const getBackgroundColor = ({
  $disabled,
  $outlined,
  $variant,
}: Pick<StyledButtonRoundedProps, '$disabled' | '$outlined' | '$variant'>) => {
  if ($disabled) {
    return $outlined ? colors.white : colors.grey01200;
  }

  if ($outlined) return colors.white;
  if ($variant === 'danger') return colors.red;

  return colors.green01400;
};

const getBackgroundColorHover = ({
  $outlined,
  $variant,
}: Pick<StyledButtonRoundedProps, '$outlined' | '$variant'>) => {
  if ($outlined) {
    if ($variant === 'danger') return colors.red01200;
    if ($variant === 'dotted') return colors.grey01100;

    return colors.green01200;
  }

  return $variant === 'danger' ? colors.red01700 : colors.green01700;
};

// TODO: extract types and use without $ prefix
// $disabled -> isDisabled
const getBorderColor = ({
  $disabled,
  $variant,
  isHovered,
}: Pick<StyledButtonRoundedProps, '$disabled' | '$variant'> & { isHovered?: boolean }) => {
  if ($disabled) return colors.grey01700;
  if ($variant === 'danger') return colors.red01900;
  if ($variant === 'dotted') {
    if (isHovered) return colors.grey01500;
    return colors.azure84;
  }

  return colors.green01400;
};

const getBorderType = ({ $variant }: Pick<StyledButtonRoundedProps, '$variant'>) => {
  if ($variant === 'dotted') return 'dotted';
  return 'solid';
};

const getPadding = ({
  $sizeHorizontal,
  $sizeVertical,
  $withIcon,
  $variant,
}: Pick<StyledButtonRoundedProps, '$sizeHorizontal' | '$sizeVertical' | '$withIcon' | '$variant'>) => {
  if ($variant === 'primary-new' || $variant === 'dotted') return '8px 10px';

  const horizontalSizes = { small: 12, medium: 22, large: 28 };
  const verticalSizes = { small: 6, medium: 8, large: 10 };

  const paddingIconOffset = $withIcon ? 4 : 0;
  const offsetHorizontal = horizontalSizes[$sizeHorizontal];
  const offsetVertical = verticalSizes[$sizeVertical];

  return `${offsetVertical}px ${paddingIconOffset + offsetHorizontal}px ${offsetVertical}px ${offsetHorizontal}px`;
};

export const StyledButtonRounded = styled.div<StyledButtonRoundedProps>`
  position: relative;
  width: ${(props) => (props.$isFullWidth ? '100%' : 'fit-content')};

  .button {
    width: ${(props) => (props.$isFullWidth ? '100%' : 'fit-content')};
    border-radius: 8px;
    white-space: nowrap;
    overflow: hidden;
    padding: ${(props) => getPadding(props)};

    color: ${(props) => getTextColor(props)};
    background-color: ${(props) => getBackgroundColor(props)};
    border: 1px ${(props) => getBorderType(props)} ${(props) => getBorderColor(props)};

    transition: background-color 0.2s ease;

    .button-content {
      gap: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      white-space: nowrap;
      visibility: ${(props) => (props.$isLoading ? 'hidden' : 'visible')};
    }

    ${(props) =>
      !props.$disabled &&
      `
      &:hover {
        cursor: pointer;
        background-color: ${getBackgroundColorHover(props)};
        border: 1px ${getBorderType(props)} ${getBorderColor({ ...props, isHovered: true })};
      }
    `}
  }

  .loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: ${(props) => (props.$isLoading ? 'block' : 'none')};
  }
`;
